import { Component, type ComponentChildren } from 'preact';

import { logComponentError } from 'Diagnostics/diagnostics';

interface Props {
  name?: string;
  children: ComponentChildren;
}

/**
 * Lightweight React Error Boundary that catches render/lifecycle errors
 * in its subtree and logs them to Loki via the diagnostics module.
 *
 * On error the subtree is replaced with nothing (renders null) so the
 * rest of the page continues to work. This is deliberately non-intrusive:
 * the broken widget silently disappears rather than crashing the whole app.
 *
 * Usage:
 *   <ErrorBoundary name="SearchPage">
 *     <SearchPage ... />
 *   </ErrorBoundary>
 */
export default class ErrorBoundary extends Component<Props> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }): void {
    try {
      logComponentError({
        componentName: this.props.name || 'unknown',
        error,
        componentStack: info?.componentStack,
      });
    } catch {
      // diagnostics must never break the app
    }
  }

  render(): ComponentChildren {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}
